import { useSearchParams } from 'react-router-dom'
import { Menu, MenuButton, MenuItem, MenuItems, Transition } from '@headlessui/react'
import { Fragment, useState } from 'react'
import { CheckIcon, PersonCircleIcon } from '$components'
import { cn } from '$components'
import { ImageCircle } from '$components'
import { User } from '$api-client/types.gen'
import { personSelectFilterIconStyling, personSelectFilterTextStyling, PersonSelectFilterTriggerStyling, personSelectFilterTriggerStyling } from './styling'

export type ValueOf<T> = T[keyof T]

export const Strategy = {
  Query: 'query',
  State: 'state',
} as const

export type PersonSelectFilterProps = {
  options: User[]
  size?: NonNullable<PersonSelectFilterTriggerStyling>['size']
} & (
  | {
    strategy: typeof Strategy.Query
    queryParamKey: string
  }
  | {
    strategy: typeof Strategy.State
    value: number[]
    onChange: (selectedIds: number[]) => void
  }
)

const MAX_ICON_DISPLAY = 4

export function PersonSelectFilter(props: PersonSelectFilterProps) {
  const { options } = props
  const strategy = props.strategy ?? Strategy.Query
  const size = props.size ?? 'md'

  const [searchParams, setSearchParams] = useSearchParams()
  const [internalSelectedPeopleIds, setInternalSelectedPeopleIds] = useState<number[]>(
    props.strategy === Strategy.State ? props.value : [],
  )

  const selectedPeopleIds = strategy === Strategy.Query && props.strategy === Strategy.Query // Ensure props.queryParamKey is accessible
    ? searchParams.getAll(props.queryParamKey).map(Number)
    : internalSelectedPeopleIds

  const selectedPeople = options.filter(person => selectedPeopleIds.includes(person.id))

  const handleSelectPerson = (person: User) => {
    if (props.strategy === Strategy.Query) {
      return setSearchParams(prev => {
        const currentIds = prev.getAll(props.queryParamKey)
        if (currentIds.includes(person.id.toString())) {
          prev.delete(props.queryParamKey, person.id.toString())
        } else {
          prev.append(props.queryParamKey, String(person.id))
        }
        return prev
      })
    }

    if (props.strategy === Strategy.State) {
      const newSelectedIds = selectedPeopleIds.includes(person.id)
        ? selectedPeopleIds.filter(id => id !== person.id)
        : [...selectedPeopleIds, person.id]

      setInternalSelectedPeopleIds(newSelectedIds)
      props.onChange?.(newSelectedIds)
    }
  }

  const handleClearPerson = () => {
    if (props.strategy === Strategy.Query) {
      setSearchParams(prev => {
        prev.delete(props.queryParamKey)
        return prev
      })
    } else if (props.strategy === Strategy.State) {
      setInternalSelectedPeopleIds([])
      props.onChange([])
    }
  }

  return (
    <Menu>
      <MenuButton className={cn(personSelectFilterTriggerStyling({ size }))}>
        { selectedPeople.length === 0 && (
          <span className={cn(personSelectFilterIconStyling({ size }), 'text-gray-600')}>
            <PersonCircleIcon className="size-4" />
          </span>
        )}
        <div className={cn(personSelectFilterTextStyling({ size }))}>
          <div className="flex">
            {selectedPeople.slice(0, MAX_ICON_DISPLAY).map((person, key) => (
              // person.image
              //   ? (
              //     <img
              //       key={key}
              //       src={person.image}
              //       className={cn(
              //         'size-5 rounded-full outline outline-2 outline-white',
              //         {
              //           '-ml-1': key !== 0,
              //         },
              //       )}
              //     />
              //   )
              //   : (
              <div
                key={key}
                className={cn({
                  '-ml-1': key !== 0,
                })}
              >
                <ImageCircle
                  size={size}
                  image={person.image}
                />
              </div>
              // )
            ))}
          </div>
          <div>
            { selectedPeople.length === 1 && (
              <span className="block max-w-full sm:max-w-24 text-nowrap line-clamp-1 text-ellipsis">
                { selectedPeople[0].first_name + ' ' + selectedPeople[0].last_name }
              </span>
            ) }
            { selectedPeople.length === 0 && 'All' }
            { selectedPeople.length > MAX_ICON_DISPLAY && (
              <span>
                + { selectedPeople.length - MAX_ICON_DISPLAY }
              </span>
            )}
          </div>
        </div>
      </MenuButton>

      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <MenuItems
          anchor="bottom start"
          className="z-50 shadow-xl rounded-xl"
        >
          <div className="flex flex-col bg-white border border-gray-100 rounded-xl text-sm w-44 mt-1 h-full max-h-48 overflow-y-auto">
            <MenuItem>
              <button
                onClick={handleClearPerson}
                className="w-full text-left py-2 px-3 text-gray-700 hover:bg-gray-50/50 [&:not(:last-child)]:border-b border-gray-100"
              >
                All
              </button>
            </MenuItem>

            { options.map((person, key) => (
              <MenuItem key={key}>
                <button
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    handleSelectPerson(person)
                  }}
                  className={cn(
                    'flex text-left py-2 px-3 hover:bg-gray-50/50 text-gray-700 items-center gap-1.5',
                    'relative',
                  )}
                >
                  <span className="absolute top-1/2 -translate-y-1/2 right-2">
                    { selectedPeopleIds.includes(person.id) && (
                      <CheckIcon className="size-4 text-indigo-400" />
                    )}
                  </span>

                  <ImageCircle
                    size="sm"
                    image={person.image}
                  />

                  <span className="truncate mr-5">
                    { person.first_name + ' ' + person.last_name }
                  </span>
                </button>
              </MenuItem>
            ))}
          </div>
        </MenuItems>
      </Transition>
    </Menu>
  )
}
